import React, { useState, useRef, useEffect } from "react"
import {
  Modal, Button, Select, message,
} from "antd"
import { FileTextOutlined } from "@ant-design/icons"
import reiNormal from "../../../assets/images/evidence-rie.svg"
import reiThinking from "../../../assets/images/rie-thinking.png"

const { Option } = Select

const STEP_UPLOAD = 1
const STEP_ANALYZING = 2
const STEP_ASSESSMENT = 3
const STEP_CONFIRMING = 4
const STEP_RATING = 5

const RATING_OPTIONS = [
  { value: 0, label: "1 - INITIATING" },
  { value: 1, label: "2 - Developing" },
  { value: 2, label: "3 - Proficient" },
  { value: 3, label: "4 - Well Developed" },
]

const FileUploadModal = ({
  visible,
  onClose,
  onSubmit,
  isLoading = false,
  initialFile = null,
}) => {
  const [step, setStep] = useState(STEP_UPLOAD)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [selectedRating, setSelectedRating] = useState(null)
  const [aiSuggestedRating, setAiSuggestedRating] = useState(null)
  const fileInputRef = useRef(null)
  const dropZoneRef = useRef(null)
  const resetState = () => {
    setStep(STEP_UPLOAD)
    setUploadedFile(null)
    setSelectedRating(null)
    setAiSuggestedRating(null)
  }
  useEffect(() => {
    if (!visible) {
      resetState()
    }
  }, [visible])
  const analyzeFile = (file) => {
    setStep(STEP_ANALYZING)

    const formData = new FormData()
    formData.append("file", file)

    APIClient.post(
      "/evidence/analyze_for_chatbot",
      formData,
      (response) => {
        const ratingVal = response.rating !== null && response.rating !== undefined
          ? response.rating - 1
          : null

        setAiSuggestedRating(ratingVal)
        setStep(STEP_ASSESSMENT)
      },
      () => {
        setAiSuggestedRating(null)
        setStep(STEP_ASSESSMENT)
      },
    )
  }
  // If a file is provided externally, skip Step 1 and go straight to analyzing
  useEffect(() => {
    if (visible && initialFile) {
      setUploadedFile(initialFile)
      analyzeFile(initialFile)
    }
  }, [visible, initialFile])

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleFileSelect = (file) => {
    if (!file) return
    setUploadedFile(file)
    analyzeFile(file)
  }

  const handleDropZoneClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileInputChange = (e) => {
    const file = e.target.files && e.target.files[0]
    if (file) handleFileSelect(file)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const file = e.dataTransfer.files && e.dataTransfer.files[0]
    if (file) handleFileSelect(file)
  }

  const handleAssessmentContinue = () => {
    setStep(STEP_CONFIRMING)
  }
  const handleFinalUpload = (rating) => {
    if (rating === null || rating === undefined) {
      message.warning("Please select a rating before uploading.")
      return
    }
    onSubmit(uploadedFile, rating)
  }

  const handleConfirmChanges = () => {
    if (aiSuggestedRating !== null && aiSuggestedRating !== undefined) {
      setSelectedRating(aiSuggestedRating)
      handleFinalUpload(aiSuggestedRating)
    } else {
      setStep(STEP_RATING)
    }
  }

  const handleUpload = () => {
    if (selectedRating === null || selectedRating === undefined) {
      message.warning("Please select a rating before uploading.")
      return
    }
    handleFinalUpload(selectedRating)
  }

  const getRatingLabel = (ratingValue) => {
    const opt = RATING_OPTIONS.find((o) => o.value === ratingValue)
    return opt ? opt.label : "?"
  }

  const FileChip = () => {
    if (!uploadedFile) return null
    return (
      <div className="evidence-file-chip">
        <FileTextOutlined className="evidence-file-chip__icon" />
        <span className="evidence-file-chip__name">{uploadedFile.name}</span>
      </div>
    )
  }

  const ReiMascot = ({ showSpeechBubble = false }) => {
    if (showSpeechBubble) {
      return (
        <div className="rei-mascot-container">
          <img
            src={reiNormal}
            alt="Rei mascot"
            className="rei-mascot-image rei-mascot-image--in-container"
          />
          <img
            src={reiThinking}
            alt="Rei is thinking"
            className="rei-thinking-bubble"
          />
        </div>
      )
    }
    return (
      <img src={reiNormal} alt="Rei mascot" className="rei-mascot-image" />
    )
  }

  const renderStepUpload = () => (
    <div className="evidence-step evidence-step--upload">
      <div
        ref={dropZoneRef}
        className="evidence-drop-zone"
        onClick={handleDropZoneClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && handleDropZoneClick()}
      >
        <FileTextOutlined className="evidence-drop-zone__icon" />
        <p className="evidence-drop-zone__text">
          Click or drag file here to upload
        </p>
        <p className="evidence-drop-zone__hint">
          Supports images (JPG, PNG), PDFs, CSV, and documents
        </p>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleFileInputChange}
          accept="image/*,.pdf,.csv,.xlsx,.xls,.doc,.docx,.txt"
        />
      </div>
    </div>
  )

  const renderStepAnalyzing = () => (
    <div className="evidence-step evidence-step--analyzing">
      <FileChip />
      <ReiMascot showSpeechBubble />
      <div className="rei-analyzing-box">
        <span className="rei-analyzing-box__dot" />
        <span className="rei-analyzing-box__dot" />
        <span className="rei-analyzing-box__dot" />
        Rei is analyzing...
      </div>
    </div>
  )

  const renderStepAssessment = () => (
    <div className="evidence-step evidence-step--assessment">
      <FileChip />
      <ReiMascot />
      <div className="rei-assessment-card">
        <h4 className="rei-assessment-card__title">Rei&apos;s Assessment</h4>
        <p className="rei-assessment-card__body">
          {aiSuggestedRating !== null && aiSuggestedRating !== undefined ? (
            <>
              Rei has analyzed your evidence and suggests a rating of{" "}
              <strong>{getRatingLabel(aiSuggestedRating)}</strong> for this
              indicator. Click Continue to proceed.
            </>
          ) : (
            "Rei has analyzed your evidence but could not determine a rating. You will be asked to select one manually."
          )}
        </p>
      </div>
    </div>
  )

  const renderStepConfirming = () => (
    <div className="evidence-step evidence-step--confirming">
      <FileChip />
      <ReiMascot />
      <div className="rei-assessment-card">
        <h4 className="rei-assessment-card__title">
          Your Rating is being confirmed!
        </h4>
        <p className="rei-assessment-card__body">
          {aiSuggestedRating !== null && aiSuggestedRating !== undefined ? (
            <>
              The suggested rating is{" "}
              <strong>{getRatingLabel(aiSuggestedRating)}</strong>. Click
              &ldquo;Confirm &amp; Upload&rdquo; to save this evidence.
            </>
          ) : (
            "No rating was suggested. Click \"Confirm\" to manually select a rating."
          )}
        </p>
      </div>
    </div>
  )

  const renderStepRating = () => (
    <div className="evidence-step evidence-step--rating">
      <FileChip />
      <ReiMascot />
      <div className="rei-assessment-card">
        <h4 className="rei-assessment-card__title">Select Your Rating</h4>
        <p className="rei-assessment-card__body">
          A rating is required to upload evidence. Rei was unable to determine a
          rating. Please choose a rating from the dropdown below (1–4).
        </p>
      </div>
      <div className="evidence-field-group">
        <label className="evidence-field-label">
          Rating Level{" "}
          <span className="evidence-field-label__hint">
            (Select between 1 – 4)
          </span>
        </label>
        <Select
          className="evidence-select"
          value={selectedRating}
          onChange={(val) => setSelectedRating(val)}
          placeholder="Select a rating"
        >
          {RATING_OPTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      </div>
    </div>
  )

  const primaryBtnStyle = {
    background: "#a6192e",
    borderColor: "#a6192e",
    color: "#fff",
    borderRadius: "6px",
    height: "40px",
    padding: "0 20px",
    fontWeight: 600,
  }

  const cancelBtnStyle = {
    background: "#fff",
    borderColor: "#d0d0d0",
    color: "#333",
    borderRadius: "6px",
    height: "40px",
    padding: "0 20px",
  }

  const renderFooter = () => {
    if (step === STEP_UPLOAD) {
      return [
        <Button key="cancel" onClick={handleClose} style={cancelBtnStyle}>
          Cancel
        </Button>,
      ]
    }
    if (step === STEP_ANALYZING) return null
    if (step === STEP_ASSESSMENT) {
      return [
        <Button key="cancel" onClick={handleClose} style={cancelBtnStyle}>
          Cancel
        </Button>,
        <Button
          key="continue"
          style={primaryBtnStyle}
          onClick={handleAssessmentContinue}
        >
          Continue
        </Button>,
      ]
    }
    if (step === STEP_CONFIRMING) {
      return [
        <Button key="cancel" onClick={handleClose} style={cancelBtnStyle}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          style={primaryBtnStyle}
          loading={isLoading}
          onClick={handleConfirmChanges}
        >
          Confirm &amp; Upload
        </Button>,
      ]
    }
    if (step === STEP_RATING) {
      return [
        <Button key="cancel" onClick={handleClose} style={cancelBtnStyle}>
          Cancel
        </Button>,
        <Button
          key="upload"
          style={primaryBtnStyle}
          loading={isLoading}
          onClick={handleUpload}
          disabled={selectedRating === null || selectedRating === undefined}
        >
          Upload
        </Button>,
      ]
    }
    return null
  }

  return (
    <Modal
      title="Attach Evidence"
      open={visible}
      onCancel={handleClose}
      footer={renderFooter()}
      width={520}
      className="evidence-upload-modal"
      maskClosable={step !== STEP_ANALYZING}
      closable={step !== STEP_ANALYZING}
    >
      <p className="evidence-modal-subtitle">
        Please specify the indicator and rating for this evidence.
      </p>
      {step === STEP_UPLOAD && renderStepUpload()}
      {step === STEP_ANALYZING && renderStepAnalyzing()}
      {step === STEP_ASSESSMENT && renderStepAssessment()}
      {step === STEP_CONFIRMING && renderStepConfirming()}
      {step === STEP_RATING && renderStepRating()}
    </Modal>
  )
}

export default FileUploadModal
